import { useState, useMemo, useCallback } from "react";
import { useHR } from "../context/HRContext";
import { useAuthStore } from "@/store/authStore";
import toast from "react-hot-toast";

const today = new Date().toISOString().split("T")[0];

export function useAttendance() {
  const { employees, attendance, markAttendance, bulkMarkAttendance } = useHR();
  const { user } = useAuthStore();

  const [selectedDate, setSelectedDate] = useState(today);
  const [filterEmp, setFilterEmp] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [isMarkOpen, setIsMarkOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    employeeId: "", date: today,
    status: "present",
    checkIn: "", checkOut: "", remarks: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Records for the selected date
  const dayRecords = useMemo(() => {
    const target = selectedDate;
    return attendance.filter((a) => {
      if (!a.date) return false;
      const d = new Date(a.date);
      if (isNaN(d.getTime())) return false;
      return d.toISOString().split("T")[0] === target;
    });
  }, [attendance, selectedDate]);

  // Employees with their attendance for the selected date
  const employeeAttendance = useMemo(() => {
    return employees.map((emp) => {
      const rec = dayRecords.find((a) => a.employeeId === emp.id);
      return { ...emp, attendance: rec || null };
    });
  }, [employees, dayRecords]);

  const filtered = useMemo(() => {
    return dayRecords.filter((rec) => {
      const q = filterEmp.toLowerCase().trim();
      const matchEmp = !q || rec.employeeName?.toLowerCase().includes(q) || rec.employeeId?.toLowerCase().includes(q);
      
      const recStatus = rec.status || (rec.checkIn ? "present" : "absent");
      const matchStatus = !filterStatus || recStatus === filterStatus;
      
      return matchEmp && matchStatus;
    });
  }, [dayRecords, filterEmp, filterStatus]);

  const stats = useMemo(() => {
    const recs = dayRecords;
    const present = recs.filter((r) => r.status === "present" || r.status === "late" || (!r.status && r.checkIn)).length;
    const absent = recs.filter((r) => r.status === "absent" || (!r.status && !r.checkIn)).length;
    const halfDay = recs.filter((r) => r.status === "half_day").length;
    const onLeave = recs.filter((r) => r.status === "on_leave").length;
    return {
      total: employees.length,
      marked: recs.length,
      present,
      absent,
      halfDay,
      onLeave,
    };
  }, [dayRecords, employees]);

  const openMark = useCallback((emp = null, existing = null) => {
    if (existing) {
      setEditingRecord(existing);
      setFormData({ ...existing });
    } else {
      const defaultEmp = emp || employees.find((e) => e.id === user?.employeeId) || null;
      setEditingRecord(null);
      setFormData({
        employeeId: defaultEmp?.id || "",
        employeeName: defaultEmp?.name || "",
        date: selectedDate,
        status: "present",
        checkIn: "", checkOut: "", remarks: "",
      });
    }
    setFormErrors({});
    setIsMarkOpen(true);
  }, [selectedDate]);

  const closeMark = useCallback(() => {
    setIsMarkOpen(false);
    setEditingRecord(null);
    setFormErrors({});
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setFormErrors((p) => ({ ...p, [field]: undefined }));
  }, []);

  const validate = (data) => {
    const errs = {};
    if (!data.employeeId) errs.employeeId = "Employee is required";
    if (!data.date) errs.date = "Date is required";
    return errs;
  };

  const save = useCallback(async () => {
    const errs = validate(formData);
    if (Object.keys(errs).length) { setFormErrors(errs); return false; }
    setIsSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      const emp = employees.find((e) => e.id === formData.employeeId);
      await markAttendance({ ...formData, employeeName: emp?.name || formData.employeeName });
      toast.success("Attendance marked successfully");
      closeMark();
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [formData, markAttendance, employees, closeMark]);

  const saveAll = useCallback(async (records) => {
    setIsSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 400));
      await bulkMarkAttendance(records);
      toast.success("Bulk attendance marked successfully");
      setIsBulkOpen(false);
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [bulkMarkAttendance]);

  return {
    selectedDate, setSelectedDate,
    filterEmp, setFilterEmp,
    filterStatus, setFilterStatus,
    filtered, stats, employees, dayRecords, employeeAttendance,
    isMarkOpen, isBulkOpen, setIsBulkOpen,
    editingRecord, formData, formErrors, isSaving,
    openMark, closeMark, updateField, save, saveAll,
  };
}
